import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(description: string, lineSpacing: number, bScale: number) {
  if (!description) return null;
  const lines = description.split('\n').map((line) => line.trim()).filter(Boolean);
  const style = { fontSize: `${0.75 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="mt-1 whitespace-pre-line text-slate-600" style={style}>{description}</p>;
  }
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-slate-600" style={style}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*\s]+/, '')}</li>)}
    </ul>
  );
}

export function TwoColumnTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="grid min-h-full grid-cols-[31%_69%] bg-white text-slate-800"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <aside className="bg-slate-900 px-6 py-8 text-slate-100" style={{ padding: `0.75in 0.5in 0.75in 0.5in` }}>
        {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mb-4 border-2 border-slate-100" />}
        <h1 className="font-bold leading-tight tracking-wide" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.5 * hScale}rem` }}>{personal.fullName || 'Your Name'}</h1>

        <section className="mt-7">
          <h2 className="border-b border-slate-500 pb-1.5 font-bold uppercase tracking-[0.2em]" style={{ fontSize: `${0.65 * hScale}rem` }}>Contact</h2>
          <div className="mt-3 space-y-1.5 break-words text-slate-300" style={{ fontSize: `${0.65 * bScale}rem` }}>
            {personal.email && <p>{personal.email}</p>}
            {personal.phone && <p>{personal.phone}</p>}
            {personal.location && <p>{personal.location}</p>}
            {personal.website && <p>{personal.website}</p>}
            {personal.linkedin && <p>{personal.linkedin}</p>}
          </div>
        </section>

        {skills.length > 0 && (
          <section className="mt-7">
            <h2 className="border-b border-slate-500 pb-1.5 font-bold uppercase tracking-[0.2em]" style={{ fontSize: `${0.65 * hScale}rem` }}>Skills</h2>
            <div className="mt-3 space-y-2" style={{ fontSize: `${0.65 * bScale}rem` }}>
              {skills.map((skill) => (
                <div key={skill.id}>
                  <span className="font-semibold">{skill.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section className="mt-7">
            <h2 className="border-b border-slate-500 pb-1.5 font-bold uppercase tracking-[0.2em]" style={{ fontSize: `${0.65 * hScale}rem` }}>Education</h2>
            <div className="mt-3 space-y-4" style={{ fontSize: `${0.65 * bScale}rem` }}>
              {education.map((item) => (
                <article>
                  <h3 className="font-bold text-white" style={{ fontSize: `${0.75 * hScale}rem` }}>{item.school}</h3>
                  <p className="mt-0.5 text-slate-300">{item.degree}{item.field && `, ${item.field}`}</p>
                  <p className="text-slate-400">
                    {formatDate(item.startDate, s.dateFormat)} - {formatDate(item.endDate, s.dateFormat)}
                  </p>
                  {item.location && <p className="text-slate-400">{item.location}</p>}
                  {item.description && <p className="mt-1 whitespace-pre-line text-slate-300" style={{ lineHeight: s.spacing.lineSpacing }}>{item.description}</p>}
                </article>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section className="mt-7">
            <h2 className="border-b border-slate-500 pb-1.5 font-bold uppercase tracking-[0.2em]" style={{ fontSize: `${0.65 * hScale}rem` }}>Certifications</h2>
            <div className="mt-3 space-y-3" style={{ fontSize: `${0.65 * bScale}rem` }}>
              {certifications.map((certification) => (
                <article key={certification.id}>
                  <h3 className="font-semibold text-white" style={{ fontSize: `${0.75 * hScale}rem` }}>{certification.name}</h3>
                  <p className="text-slate-400">{certification.issuer}{certification.date && ` | ${certification.date}`}</p>
                  {certification.url && <p className="break-words text-slate-300">{certification.url}</p>}
                </article>
              ))}
            </div>
          </section>
        )}
      </aside>

      <main className="px-8 py-8" style={{ padding: pagePadding(s.margins) }}>
        {personal.summary && (
          <section className="border-b border-slate-200 pb-5">
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-900" style={{ fontSize: `${0.65 * hScale}rem` }}>Profile</h2>
            <p className="mt-2 whitespace-pre-line text-slate-600" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section className="mt-5">
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-900" style={{ fontSize: `${0.65 * hScale}rem` }}>Experience</h2>
            <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((item) => (
                <article key={item.id}>
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{item.title}</h3>
                    <span className="shrink-0 font-semibold uppercase text-slate-500" style={{ fontSize: `${0.6 * bScale}rem` }}>
                      {formatDate(item.startDate, s.dateFormat)} - {item.current ? 'Present' : formatDate(item.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="font-semibold text-slate-700" style={{ fontSize: `${0.75 * bScale}rem` }}>{item.company}{item.location && ` / ${item.location}`}</p>
                  {renderDescription(item.description, s.spacing.lineSpacing, bScale)}
                </article>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section className="mt-6 border-t border-slate-200 pt-5">
            <h2 className="font-bold uppercase tracking-[0.2em] text-slate-900" style={{ fontSize: `${0.65 * hScale}rem` }}>Projects</h2>
            <div className="mt-3" style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((project) => (
                <article key={project.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${0.875 * hScale}rem` }}>{project.name}</h3>
                    {project.url && <span className="break-all text-slate-500" style={{ fontSize: `${0.6 * bScale}rem` }}>{project.url}</span>}
                  </div>
                  {project.description && <p className="mt-1 whitespace-pre-line text-slate-600" style={{ fontSize: `${0.75 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{project.description}</p>}
                  {project.technologies.length > 0 && <p className="mt-1 font-semibold uppercase tracking-wide text-slate-500" style={{ fontSize: `${0.6 * bScale}rem` }}>{project.technologies.join(' / ')}</p>}
                </article>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
